'use client'

import { useTranslation } from 'next-i18next'
import { useMemo } from 'react'

import type { PLTypedWidgetProps } from '@src/components/pl-base-widget'
import { usePdpSelectionDerived } from '@src/components/pl-base-widget/contexts/pdp-selection'
import { useLayoutContext } from '@src/components/pl-page-context'
import TransparentPricingC from '@src/quince-components/src/atoms/transparentPricing'
import {
  buildTransparentPricingItems,
  type TransparentPricingCostFields,
} from '@src/quince-components/src/atoms/transparentPricing/transparent-pricing.helpers'
import CUSTOM_SECTION_CONTAINER_NAMES from '@src/quince-site-common/src/constants/customSectionContainerNames'
import styles from '@src/quince-site-pdp/src/organisms/productPageSections/transparentPricing/transparentPricing.module.scss'

import { usePdpSectionImpressionRef, usePlProductProperties } from '../use-ia-impression'

import type { TransparentPricingWidgetData } from '@lastbrand/storefront-proto-registry/api/v1/widget'

// Telemetry tokens — module-level consts (not inline literals) so they read as identifiers and stay
// clear of i18next/no-literal-string without suppression comments.
const TRANSPARENT_PRICING_ACTION = 'product_transparent_pricing'
const TRANSPARENT_PRICING_TRACKING_ID = 'product-transparent-pricing'
const TRANSPARENT_PRICING_DISPLAY_NAME = 'Product Transparent Pricing'

// Hoisted so the object identity stays stable across renders — otherwise a fresh literal each render
// churns the scroll listener in `useTrackIsElementVisible` (a PDP re-renders on color/size selection).
const TRANSPARENT_PRICING_IMPRESSION_CONFIG = {
  action: TRANSPARENT_PRICING_ACTION,
  container: {
    contentContainerName: CUSTOM_SECTION_CONTAINER_NAMES.TRANSPARENT_PRICING,
    trackingId: TRANSPARENT_PRICING_TRACKING_ID,
    contentContainerDisplayName: TRANSPARENT_PRICING_DISPLAY_NAME,
  },
}

export function TransparentPricingWidgetComponent(props: PLTypedWidgetProps<TransparentPricingWidgetData>) {
  const { t } = useTranslation('common')
  const data = props.data.transparentPricingData
  const derived = usePdpSelectionDerived()
  const isBundle = useLayoutContext()?.pdp?.productType === 'Bundle'

  const selectedVariantId = derived?.selectedVariantId
  const bundleSelections = derived?.bundleSelections

  // Cost rows: normal → the `selectedVariantId` variant; bundle → per-child costs summed field-by-field,
  // mirroring non-PL `TransparentPricingBundle` (incl. its deliberate omission of `dutyFee`).
  const costs = useMemo((): TransparentPricingCostFields | undefined => {
    const products = data?.products
    if (!products) return undefined

    if (bundleSelections) {
      // Unresolved children contribute 0 — non-PL renders $0.00 rows pre-selection, not a hidden section.
      const summed = { materials: 0, hardware: 0, crafting: 0, packaging: 0, shippingHandling: 0, creditCardFees: 0 }
      for (const { productId, variantId } of bundleSelections) {
        if (variantId === undefined) continue
        const variant = products[productId]?.variants.find(v => v.variantId === variantId)
        if (!variant) continue
        summed.materials += variant.materials ?? 0
        summed.hardware += variant.hardware ?? 0
        summed.crafting += variant.crafting ?? 0
        summed.packaging += variant.packaging ?? 0
        summed.shippingHandling += variant.shippingHandling ?? 0
        summed.creditCardFees += variant.creditCardFees ?? 0
      }
      return summed
    }

    if (selectedVariantId === undefined) return undefined
    for (const product of Object.values(products)) {
      const match = product.variants.find(v => v.variantId === selectedVariantId)
      if (match) return match
    }
    return undefined
  }, [data, selectedVariantId, bundleSelections])

  const items = useMemo(() => buildTransparentPricingItems(costs, t), [costs, t])

  const quincePrice = derived?.quincePrice
  const traditionalPrice = derived?.traditionalPrice ?? 0

  // IA-only `product_transparent_pricing` impression on scroll-into-view — mirrors the non-PL
  // TransparentPricing section (`entity_id: product_transparent_pricing`, `event_type: is_visible`),
  // including the `productProperties` built from the selected variant (non-PL parity). Skipped on
  // bundles: the non-PL `TransparentPricingBundle` renders the section WITHOUT any impression, so
  // firing here would emit an event the legacy bundle page never emits.
  const productProperties = usePlProductProperties('selected')
  const impressionRef = usePdpSectionImpressionRef(TRANSPARENT_PRICING_IMPRESSION_CONFIG, productProperties, isBundle)

  // A bundle renders only once BUNDLE_PURCHASE has published its per-child selections — until then
  // `costs` is undefined and we bail below anyway. The old blanket `isBundle` bail is gone: it existed
  // because nothing published a bundle selection, so the single-variant lookup silently matched the
  // FIRST child's variant and would have shown one child's costs as the whole bundle's.
  if (isBundle && !bundleSelections) return null
  if (quincePrice === undefined || !costs) return null
  return (
    <div className={styles.transparentPricing} ref={impressionRef}>
      <TransparentPricingC items={items} quincePrice={quincePrice} traditionalPrice={traditionalPrice} />
    </div>
  )
}
